import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Col, Row, Input, DatePicker, Tabs, Select, Modal, Table, Space, Tag, Switch } from 'antd';
import type { TabsProps } from 'antd';
// import { useSelector } from 'react-redux';
import { LeftOutlined, PlusCircleOutlined, EditOutlined, SaveOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'moment/locale/lt';
import locale from 'antd/es/date-picker/locale/lt_LT';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { AppDispatch, CombinedStates } from '../../core/types';
import Main from '../../components/layout/Main';
import { updateSurvey, createSurveyQuestion, updateSurveyQuestion } from '../../core/survey/surveyApi';
import { resetSurveyCreated } from '../../core/survey/surveySlice';
import { Question } from '../../core/survey/types';
import SortableRow from '../../components/DataDisplay/SortableRow';
import DragHandle from '../../components/DataDisplay/DragHandle';
import SurveyAnswers from '../../components/SurveyAnswers';
import SurveyStatistics from '../../components/SurveyStatistics';
import AIDataAnalysis from '../../components/AIDataAnalysis';

import './surveyEdit.scss';

const SurveyEdit = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [isQuestionModalVisible, setIsQuestionModalVisible] = useState(false);
    const [questionOptions, setQuestionOptions] = useState<{ title: string }[]>([]);
    const [questionType, setQuestionType] = useState<string | null>(null);
    const [optionInput, setOptionInput] = useState('');
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [questionForm] = Form.useForm();
    const [form] = Form.useForm();

    const {
        singleSurvey,
        isFetching,
        isSurveyCreated,
    } = useSelector((state:CombinedStates) => state.survey);

    const {
        userData,
    } = useSelector((state:CombinedStates) => state.user);

    const [questions, setQuestions] = useState<Question[]>(singleSurvey?.questions || []);

    // Check if survey has parent and should show parent questions
    const hasParent = singleSurvey?.parent_id !== null && singleSurvey?.parent_id !== undefined;
    const shouldShowParentQuestions = hasParent && singleSurvey?.parent?.questions;
    const displayUpdateButton = !(singleSurvey?.is_premade === 1 && userData?.role_id === 5);

    useEffect(() => {
        if (isSurveyCreated === true) {
            dispatch(resetSurveyCreated());
        }
    }, [isSurveyCreated]);

    useEffect(() => {
        if (shouldShowParentQuestions) {
            // Show parent questions when parent exists
            setQuestions(singleSurvey?.parent?.questions || []);
        } else if (singleSurvey?.questions) {
            // Show own questions when no parent
            setQuestions(singleSurvey.questions);
        }
    }, [singleSurvey?.questions, singleSurvey?.parent?.questions, shouldShowParentQuestions]);

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = questions.findIndex(q => q.id === active.id);
            const newIndex = questions.findIndex(q => q.id === over.id);

            const newQuestions = arrayMove(questions, oldIndex, newIndex);

            setQuestions(newQuestions);
        }
    };

    const handleSaveOrder = () => {
        if (shouldShowParentQuestions || !displayUpdateButton) {
            // Don't save order for parent questions
            return;
        }
        const payload = questions.map((q, index) => ({
            id: q.id,
            order: index + 1,
        }));
        dispatch(updateSurvey({
            id: singleSurvey?.id || 0,
            questions_order: payload,
        }));
    };

    const handleCreateQuestion = () => {
        questionForm
            .validateFields()
            .then(values => {
                if (
                    (values.type === 'single' || values.type === 'multiple') && questionOptions.length === 0
                ) {
                    Modal.warning({
                        title: 'Trūksta galimų atsakymų',
                        content: 'Pridėkite bent vieną galimą atsakymo variantą prieš sukuriant klausimą.',
                    });
                    return;
                }

                dispatch(createSurveyQuestion({
                    ...values,
                    survey_id: singleSurvey?.id,
                    options: questionOptions,
                }));
                setIsQuestionModalVisible(false);
                questionForm.resetFields();
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const moveOption = (index: number, direction: 'up' | 'down') => {
        const newOptions = [...questionOptions];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (
            index < 0
            || index >= newOptions.length
            || targetIndex < 0
            || targetIndex >= newOptions.length
        ) {
            return;
        }

        const temp = newOptions[index]!; // Pasakom - jis egzistuoja
        newOptions[index] = newOptions[targetIndex]!;
        newOptions[targetIndex] = temp;

        setQuestionOptions(newOptions);
    };

    const handleUpdateQuestion = () => {
        questionForm
            .validateFields()
            .then(values => {
                if (
                    (values.type === 'single' || values.type === 'multiple') && questionOptions.length === 0
                ) {
                    Modal.warning({
                        title: 'Trūksta galimų atsakymų',
                        content: 'Pridėkite bent vieną galimą atsakymo variantą prieš atnaujindami klausimą.',
                    });
                    return;
                }
                dispatch(updateSurveyQuestion({
                    ...values,
                    id: editingQuestion?.id,
                    survey_id: singleSurvey?.id,
                    options: questionOptions.map((opt, index) => ({
                        ...opt,
                        order: index + 1,
                    })),
                }));
                setIsQuestionModalVisible(false);
                questionForm.resetFields();
                setQuestionOptions([]);
                setQuestionType(null);
                setEditingQuestion(null);
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const onFinish = (values: any) => {
        if (!singleSurvey?.id) return;
        dispatch(updateSurvey({
            ...values,
            id: singleSurvey.id,
            active_from: values.active_from ? values.active_from.format('YYYY-MM-DD HH:mm:ss') : null,
            active_until: values.active_until ? values.active_until.format('YYYY-MM-DD HH:mm:ss') : null,
        }));
    };

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];

    const handleBack = () => {
        navigate(-1); // grįžta į prieš tai buvusį puslapį
    };

    const mainTab = (
        <Card>
            <Row gutter={25}>
                <Col span={14} xs={24} sm={14}>
                    <Form
                        layout="vertical"
                        form={form}
                        onFinish={onFinish}
                        // onFinishFailed={onFinishFailed}
                        initialValues={{
                            title: singleSurvey?.title,
                            description: singleSurvey?.description,
                            start_text: singleSurvey?.start_text,
                            end_text: singleSurvey?.end_text,
                            status: singleSurvey?.status,
                            active_from: singleSurvey?.active_from ? dayjs(singleSurvey.active_from, 'YYYY-MM-DD HH:mm:ss') : null,
                            active_until: singleSurvey?.active_until ? dayjs(singleSurvey.active_until, 'YYYY-MM-DD HH:mm:ss') : null,
                            created_at: singleSurvey?.created_at,
                            updated_at: singleSurvey?.updated_at,
                        }}>

                        <Form.Item
                            label="Pavadinimas"
                            name="title"
                            rules={[{ required: true, message: 'Įveskite apklausos pavadinimą.' }]}>
                            <Input size="large" placeholder="Įveskite pavadinimą" />
                        </Form.Item>

                        <Form.Item
                            label="Aprašymas"
                            name="description">
                            <Input.TextArea size="large" placeholder="Įveskite aprašymą" rows={6} />
                        </Form.Item>

                        <Form.Item
                            label="Apklausos pradžios tekstas"
                            name="start_text">
                            <Input.TextArea
                                size="large"
                                placeholder="Įveskite tekstą, kuris bus rodomas apklausos pradžioje"
                                rows={4} />
                        </Form.Item>

                        <Form.Item
                            label="Apklausos pabaigos tekstas"
                            name="end_text">
                            <Input.TextArea
                                size="large"
                                placeholder="Įveskite tekstą, kuris bus rodomas apklausos pabaigoje"
                                rows={4} />
                        </Form.Item>

                        <Form.Item
                            label="Apklausa aktyvi nuo"
                            name="active_from">
                            <DatePicker
                                locale={locale}
                                showTime
                                size="large"
                                format="YYYY-MM-DD HH:mm"
                                style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item
                            label="Apklausa aktyvi iki"
                            name="active_until">
                            <DatePicker
                                locale={locale}
                                showTime
                                size="large"
                                format="YYYY-MM-DD HH:mm"
                                style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item
                            label="Būsena"
                            name="status"
                            rules={[{ required: true, message: 'Pasirinkite apklausos būseną.' }]}>
                            <Select size="large" placeholder="Pasirinkite būseną">
                                <Select.Option value={0}>
                                    <span><span style={{ color: '#faad14' }}>●</span> Juodraštis</span>
                                </Select.Option>
                                <Select.Option value={1}>
                                    <span><span style={{ color: '#52c41a' }}>●</span> Aktyvi</span>
                                </Select.Option>
                                <Select.Option value={3}>
                                    <span><span style={{ color: '#f5222d' }}>●</span> Sustabdyta</span>
                                </Select.Option>
                            </Select>
                        </Form.Item>

                        {displayUpdateButton && (
                            <Form.Item>
                                <Button
                                    type="primary"
                                    size="large"
                                    htmlType="submit"
                                    block
                                    loading={isFetching}>
                                    Atnaujinti apklausą
                                </Button>
                            </Form.Item>
                        )}
                    </Form>
                </Col>

                <Col span={10} xs={24} sm={10}>
                    <Card title="Apklausos informacija" size="small">
                        <p><b>Sukurta:</b> {singleSurvey?.created_at ? dayjs(singleSurvey.created_at).format('YYYY-MM-DD HH:mm') : '-'}</p>
                        <p><b>Atnaujinta:</b> {singleSurvey?.updated_at ? dayjs(singleSurvey.updated_at).format('YYYY-MM-DD HH:mm') : '-'}</p>
                        <p><b>Užpildė:</b> {singleSurvey?.submissions_count ?? 0} žmonių</p>
                        <p><b>Apklausą sukūrė:</b> {singleSurvey?.user?.first_name ?? '-'} {singleSurvey?.user?.last_name ?? '-'} ({singleSurvey?.user?.email ?? '-'})</p>
                        {singleSurvey?.is_premade === 1 && (
                            <p><b>Apklausa naudojama sukurti naują apklausą šios apklausos pagrindu.</b></p>
                        )}
                    </Card>
                </Col>
            </Row>
        </Card>
    );

    const questionsTab = (
        <Card>
            <Row justify="space-between" style={{ marginBottom: '15px' }}>
                <Col>
                    <h3>Apklausos klausimai</h3>
                    {shouldShowParentQuestions && (
                        <p style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
                            Rodomi klausimai iš pagrindinės apklausos šablono (redaguoti negalima)
                        </p>
                    )}
                </Col>
                {!hasParent && !(singleSurvey?.is_premade === 1 && userData?.role_id === 5) && (
                    <Col>
                        <Button loading={isFetching} icon={<PlusCircleOutlined />} type="primary" onClick={() => { setIsQuestionModalVisible(true); setQuestionType(''); setQuestionOptions([]); }}>
                            Pridėti klausimą
                        </Button>
                        <Button
                            loading={isFetching}
                            icon={<SaveOutlined />}
                            type="default"
                            style={{ marginLeft: '10px' }}
                            onClick={handleSaveOrder}>
                            Išsaugoti rikiavimą
                        </Button>
                    </Col>
                )}
            </Row>

            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                    items={questions.map(q => q.id)}
                    strategy={verticalListSortingStrategy}>
                    <Table
                        loading={isFetching}
                        components={{
                            body: {
                                row: SortableRow,
                            },
                        }}
                        dataSource={questions || []}
                        columns={[
                            {
                                title: '',
                                key: 'sort',
                                width: 40,
                                render: (_text, record) => (
                                    <DragHandle id={record.id} />
                                ),
                            },
                            {
                                title: 'Pavadinimas',
                                dataIndex: 'title',
                                key: 'title',
                            },
                            {
                                title: 'Tipas',
                                dataIndex: 'type',
                                key: 'type',
                                render: (type: string) => {
                                    let color = 'default';
                                    let label = '';

                                    switch (type) {
                                    case 'text':
                                        color = 'blue';
                                        label = 'Tekstinis atsakymas';
                                        break;
                                    case 'single':
                                        color = 'green';
                                        label = 'Vienas pasirinkimas';
                                        break;
                                    case 'multiple':
                                        color = 'purple';
                                        label = 'Keli pasirinkimai';
                                        break;
                                    case 'paragraph':
                                        color = 'orange';
                                        label = 'Paragrafas';
                                        break;
                                    default:
                                        label = type;
                                    }

                                    return <Tag color={color}>{label}</Tag>;
                                },
                            },
                            {
                                title: 'Sukurta',
                                dataIndex: 'created_at',
                                key: 'created_at',
                                render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: 'Veiksmai',
                                key: 'actions',
                                render: (_text, record) => (
                                    <Button
                                        style={{ zIndex: 999 }}
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            setEditingQuestion(record);
                                            setIsQuestionModalVisible(true);
                                            questionForm.setFieldsValue({
                                                title: record.title,
                                                description: record.description,
                                                type: record.type,
                                                is_required: record.is_required,
                                            });
                                            setQuestionType(record.type);
                                            setQuestionOptions(record.options || []);
                                        }}>
                                        Redaguoti
                                    </Button>
                                ),
                            },
                        ]}
                        rowKey="id"
                        pagination={false}
                        rowClassName={(record) => (record.type === 'paragraph' ? 'paragraph-row' : '')} />

                </SortableContext>
            </DndContext>

            <Modal
                title={editingQuestion ? 'Redaguoti klausimą' : 'Sukurti naują klausimą'}
                visible={isQuestionModalVisible}
                onOk={() => {
                    if (shouldShowParentQuestions || !displayUpdateButton) {
                        console.log('aaaaaaaaa');
                        // Don't save for parent questions, just close modal
                        setIsQuestionModalVisible(false);
                        questionForm.resetFields();
                        setQuestionOptions([]);
                        setQuestionType(null);
                        setEditingQuestion(null);
                    } else if (editingQuestion) {
                        handleUpdateQuestion();
                    } else {
                        handleCreateQuestion();
                    }
                }}
                onCancel={() => {
                    setIsQuestionModalVisible(false);
                    questionForm.resetFields();
                    setQuestionOptions([]);
                    setQuestionType(null);
                    setEditingQuestion(null);
                }}
                okText={(() => {
                    if (shouldShowParentQuestions) return 'Uždaryti';
                    if (singleSurvey?.is_premade === 1 && userData?.role_id === 5) return 'Uždaryti';
                    return editingQuestion ? 'Atnaujinti' : 'Sukurti';
                })()}
                cancelText="Atšaukti">
                <Form layout="vertical" form={questionForm}>
                    <Form.Item
                        label="Pavadinimas"
                        name="title"
                        rules={[{ required: true, message: 'Įveskite klausimo pavadinimą' }]}>
                        <Input placeholder={questionType === 'paragraph' ? 'Paragrafo pavadinimas' : 'Klausimo pavadinimas'} />
                    </Form.Item>

                    {questionType === 'paragraph' && (
                        <Form.Item
                            label="Aprašymas"
                            name="description">
                            <Input.TextArea placeholder="Įveskite paragrafo aprašymą" rows={4} />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Tipas"
                        name="type"
                        rules={[{ required: true, message: 'Pasirinkite klausimo tipą' }]}>
                        <Select placeholder="Pasirinkite tipą"
                            onChange={(value) => setQuestionType(value)}>
                            <Select.Option value="text">Tekstinis atsakymas</Select.Option>
                            <Select.Option value="single">Vienas pasirinkimas</Select.Option>
                            <Select.Option value="multiple">Keli pasirinkimai</Select.Option>
                            <Select.Option value="paragraph">Paragrafas</Select.Option>
                        </Select>
                    </Form.Item>

                    {questionType !== 'paragraph' && (
                        <Form.Item
                            label="Privalomas"
                            name="is_required"
                            valuePropName="checked"
                            getValueFromEvent={(checked) => (checked ? 1 : 0)}
                            getValueProps={(value) => ({ checked: value === 1 })}>
                            <Switch />
                        </Form.Item>
                    )}

                    <p style={{ fontStyle: 'italic', color: 'gray', marginBottom: 8 }}>
                        {questionType === 'single'
                            ? 'Pildydamas formą vartotojas galės pasirinkti tik vieną atsakymą iš galimų.'
                            : ''}
                        {questionType === 'multiple'
                            ? 'Pildydamas formą vartotojas galės pasirinkti kelis atsakymus iš galimų.'
                            : ''}
                        {questionType === 'paragraph'
                            ? 'Paragrafas skirtas klausimų grupavimui į atskiras sekcijas. Tai nėra klausimas, o tik pavadinimas ir aprašymas grupavimui.'
                            : ''}
                    </p>
                    {(questionType === 'single' || questionType === 'multiple') && (
                        <Form.Item label="Galimi atsakymai">
                            {displayUpdateButton && !shouldShowParentQuestions && (
                                <Space style={{ marginBottom: 8 }}>
                                    <Input
                                        value={optionInput}
                                        onChange={(e) => setOptionInput(e.target.value)}
                                        placeholder="Įveskite variantą" />
                                    <Button
                                        onClick={() => {
                                            if (optionInput.trim()) {
                                                setQuestionOptions([...questionOptions, { title: optionInput.trim() }]);
                                                setOptionInput('');
                                            }
                                        }}
                                        type="primary">
                                        Pridėti
                                    </Button>
                                </Space>
                            )}
                            {!displayUpdateButton || shouldShowParentQuestions ? (
                                <Table
                                    dataSource={questionOptions}
                                    columns={[
                                        { title: 'Atsakymo variantas', dataIndex: 'title', key: 'title' },
                                    ]}
                                    rowKey={(_, index) => index?.toString() || `tmp-${Math.random()}`}
                                    size="small"
                                    pagination={false} />
                            ) : (
                                <Table
                                    dataSource={questionOptions}
                                    columns={[
                                        { title: 'Atsakymo variantas', dataIndex: 'title', key: 'title' },
                                        {
                                            title: 'Rikiavimas',
                                            key: 'sort',
                                            render: (_text, _record, index) => (
                                                <Space>
                                                    <Button
                                                        size="small"
                                                        onClick={() => moveOption(index, 'up')}
                                                        disabled={index === 0}>
                                                        ↑
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={() => moveOption(index, 'down')}
                                                        disabled={index === questionOptions.length - 1}>
                                                        ↓
                                                    </Button>
                                                </Space>
                                            ),
                                        },
                                        {
                                            title: '',
                                            key: 'actions',
                                            render: (_text, record, index) => (
                                                <Button
                                                    type="link"
                                                    danger
                                                    onClick={() => {
                                                        setQuestionOptions(questionOptions.filter((_, i) => i !== index));
                                                    }}>
                                                    Pašalinti
                                                </Button>
                                            ),
                                        },
                                    ]}
                                    rowKey={(_, index) => index?.toString() || `tmp-${Math.random()}`}
                                    size="small"
                                    pagination={false} />
                            )}
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </Card>
    );

    const answersTab = (
        <SurveyAnswers />
    );

    const statisticsTab = (
        <SurveyStatistics />
    );

    const aiAnalysisTab = (
        <AIDataAnalysis />
    );

    // Filter tabs based on whether survey is premade and status
    const allTabs: TabsProps['items'] = [
        {
            key: '1',
            label: 'Pagrindinė informacija',
            children: mainTab,
        },
        {
            key: '2',
            label: 'Apklausos klausimai',
            children: questionsTab,
        },
        {
            key: '3',
            label: 'Apklausos atsakymai',
            children: answersTab,
        },
        {
            key: '4',
            label: 'Apklausos statistika',
            children: statisticsTab,
        },
        {
            key: '5',
            label: 'Dirbtinio intelekto duomenų analizė',
            children: aiAnalysisTab,
        },
    ];

    // Filter tabs based on conditions
    let items = allTabs;

    // Hide answers and statistics tabs if survey is premade
    if (singleSurvey?.is_premade === 1) {
        items = items.filter(tab => tab.key !== '3' && tab.key !== '4');
    }

    // Show AI analysis tab only if survey is not premade and status is 3
    if (singleSurvey?.is_premade !== 1 && singleSurvey?.status === 3) {
        // AI analysis tab is already included in allTabs, so no additional filtering needed
    } else {
        // Hide AI analysis tab if conditions are not met
        items = items.filter(tab => tab.key !== '5');
    }

    return (
        <Main
            className="edit-page"
            title={<div>Apklausos redagavimas</div>}
            headerTitle={<div>Apklausos: <span>&quot;{singleSurvey?.title}&quot;</span> redagavimas</div>}
            menuWidth={350}
            menuPlacement="left"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent={<div />}
            floatingFooter
            backLink={false}
            breadcrumb={null}>
            <Row justify="space-between">
                <Col>
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        onClick={handleBack}
                        style={{ marginBottom: '16px' }}>
                        Grįžti atgal
                    </Button>
                </Col>
                <Col>
                    <Button onClick={() => window.open(`/apklausa/pildymas/${singleSurvey?.slug}`, '_blank')} type="primary" icon={<EyeOutlined />}>
                        Peržiūrėti apklausą
                    </Button>
                </Col>
            </Row>
            <Tabs defaultActiveKey="1" items={items} type="card" />
        </Main>
    );
};

export default SurveyEdit;

SurveyEdit.propTypes = {};
